import { CartAddEvent } from '@theme/events';

class PlantitPotAdd extends HTMLElement {
  #pending = false;
  #controller;
  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('change', event => {
      if (!(event.target instanceof HTMLSelectElement)) return;
      const price = this.querySelector('[data-pot-price]');
      const selected = event.target.selectedOptions[0];
      if (price && selected?.dataset.price) price.textContent = selected.dataset.price;
    }, { signal });
    this.addEventListener('submit', event => this.#add(event), { signal });
  }
  disconnectedCallback() { this.#controller?.abort(); }
  async #add(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    if (this.#pending || !form.reportValidity()) return;
    this.#pending = true;
    const button = form.querySelector('button[type="submit"]');
    const status = this.querySelector('[data-pot-status]');
    button.disabled = true;
    button.textContent = 'Adding…';
    this.setAttribute('aria-busy', 'true');
    status.textContent = '';
    const data = new FormData(form);
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(item => item.dataset.sectionId))];
    data.set('sections', sectionIds.join(','));
    data.set('sections_url', location.pathname);
    try {
      const endpoint = new URL(form.action);
      endpoint.pathname = endpoint.pathname.replace(/\.js$/, '') + '.js';
      const response = await fetch(endpoint, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'This pot could not be added. Please try again.');
      status.textContent = 'Pot added to your cart.';
      document.dispatchEvent(new CartAddEvent({}, String(data.get('id')), {
        source: 'product-form-component', itemCount: 1, sections: result.sections
      }));
    } catch (error) {
      status.textContent = error.message || 'Please try again.';
    } finally {
      this.#pending = false;
      button.disabled = false;
      button.textContent = 'Add pot';
      this.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('plantit-pot-add')) customElements.define('plantit-pot-add', PlantitPotAdd);

class PlantitGalleryNav extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.gallery = this.closest('media-gallery');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-image-index]');
      if (!button) return;
      this.gallery.querySelector('slideshow-component')?.select(Number(button.dataset.imageIndex), event);
    }, { signal });
    this.gallery?.addEventListener('slideshow:select', (event) => {
      if (event.target !== this.gallery.querySelector('slideshow-component')) return;
      this.querySelectorAll('[data-image-index]').forEach((button) => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.imageIndex) === event.detail.index));
      });
    }, { signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
}
if (!customElements.get('plantit-gallery-nav')) customElements.define('plantit-gallery-nav', PlantitGalleryNav);

class PlantitFulfillment extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.closest('.shopify-section')?.addEventListener('variant:update', (event) => {
      const next = event.detail?.data?.html?.querySelector(`plantit-fulfillment[data-product-id="${this.dataset.productId}"]`);
      if (next) this.innerHTML = next.innerHTML;
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
}
if (!customElements.get('plantit-fulfillment')) customElements.define('plantit-fulfillment', PlantitFulfillment);

/* One observer, no scroll handler; all content remains available without motion. */
(() => {
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const seen = new WeakSet();
  const active = new Set();
  const selector = '.plantit-home__section-head,.plantit-home__category,.plantit-home__project-grid,.plantit-home__space-grid > div,.plantit-home__how li,.plantit-brief__head,.plantit-bundles__head,.plantit-instagram__head';
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      observer.unobserve(entry.target);
      if (motion.matches || !entry.target.animate) continue;
      const animation = entry.target.animate([
        { opacity: .35, transform: 'translateY(14px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], { duration: 460, easing: 'cubic-bezier(.22,.61,.36,1)' });
      active.add(animation);
      animation.finished.then(() => active.delete(animation), () => active.delete(animation));
    }
  }, { threshold: .12 }) : null;
  function observe(root = document) {
    if (!observer || motion.matches) return;
    root.querySelectorAll(selector).forEach(element => {
      if (seen.has(element)) return;
      seen.add(element);
      observer.observe(element);
    });
  }
  motion.addEventListener('change', () => {
    if (motion.matches) {
      observer?.disconnect();
      active.forEach(animation => animation.cancel());
      active.clear();
    } else observe();
  });
  observe();
  document.addEventListener('shopify:section:load', event => observe(event.target));
})();
